import { getAddressDifferences } from '../pages/location-management/LocationManagementPage/LocationSettingsTab/locationUtils'
import { Location, MonitoringServiceProviderLocation } from '../api/models'
import { hasContactDifferences } from '../pages/location-management/LocationManagementPage/LocationSettingsTab/contactUtils'

/**
 * Ensures a string value is defined by returning an empty string if the value is undefined or null.
 */
export const makeDefined = (value: string | undefined | null): string => value || ''

/**
 * Normalizes a string value by ensuring it is defined, trimming whitespace, and converting to uppercase.
 */
export const normalizeValue = (value: string | undefined | null): string => makeDefined(value).trim().toUpperCase()

/**
 * Compares two string values and determines if they are different, considering an enabled flag.
 * @param value1 The first value to compare.
 * @param value2 The second value to compare.
 * @param enabled A boolean flag indicating whether the comparison should be performed.
 * @returns True if the values are different and the comparison is enabled, otherwise false.
 */
export const hasValueDifference = (value1: string | undefined | null, value2: string | undefined | null, enabled: boolean): boolean => {
  return enabled && normalizeValue(value1) !== normalizeValue(value2)
}

const removeNonNumericRegex = /[^0-9]/g

export const hasPhoneNumberDifference = (phone1: string | undefined, phone2: string | undefined, enabled: boolean): boolean => {
  if (!enabled) return false
  // remove all non-numeric characters from the phone numbers before comparing
  const normalizedPhone1 = makeDefined(phone1).replace(removeNonNumericRegex, '').trim()
  const normalizedPhone2 = makeDefined(phone2).replace(removeNonNumericRegex, '').trim()
  return normalizedPhone1 !== normalizedPhone2
}

/**
 * This function checks for discrepancies between the location and mspLocation for both address and contacts.
 */
export const hasDiscrepancies = (
  location: Location | undefined,
  mspLocation: MonitoringServiceProviderLocation | undefined,
  enabled: boolean
): boolean => {
  if (!enabled) {
    return false
  }

  return Object.values(getAddressDifferences(location, mspLocation, enabled)).some(Boolean) || hasContactDifferences(location, mspLocation, enabled)
}
